import { AudioPlayerControls } from '@rhg/components/media/audio-player/types';
import { LoopCapability, LoopMode } from '../types';

const THREE_STATE_CYCLE: Record<LoopMode, LoopMode> = {
    [LoopMode.Off]: LoopMode.All,
    [LoopMode.All]: LoopMode.One,
    [LoopMode.One]: LoopMode.Off,
};

const TWO_STATE_CYCLE: Record<LoopMode, LoopMode> = {
    [LoopMode.Off]: LoopMode.One,
    [LoopMode.One]: LoopMode.Off,
    [LoopMode.All]: LoopMode.Off,
};

/**
 * Returns the next loop mode for the given capability's cycle.
 * Mirrors iOS `availableRepeatModes()` cycle ordering.
 */
export function nextLoopMode(
    mode: LoopMode,
    capability: LoopCapability
): LoopMode {
    if (capability === LoopCapability.RepeatAllAndOne)
        return THREE_STATE_CYCLE[mode];
    if (capability === LoopCapability.RepeatOneOnly)
        return TWO_STATE_CYCLE[mode];
    return LoopMode.Off;
}

/**
 * Loop capability is carried by the AudioPlayerControls enum value:
 * Playlist / MusicAlbum = 3-state (music album, sleep-story playlist, DTC
 * course/series), SingleLoopable / MusicSingle = 2-state, else none.
 */
export function loopCapabilityForControls(
    controls: AudioPlayerControls
): LoopCapability {
    if (
        controls === AudioPlayerControls.Playlist ||
        controls === AudioPlayerControls.MusicAlbum
    )
        return LoopCapability.RepeatAllAndOne;
    if (
        controls === AudioPlayerControls.SingleLoopable ||
        controls === AudioPlayerControls.MusicSingle
    )
        return LoopCapability.RepeatOneOnly;
    return LoopCapability.Unsupported;
}

/**
 * Whether the content type gets a shuffle button. Shuffle applies to every
 * multi-track collection (EVE-11931 / PRD Feature 2-3): `Playlist` and
 * `MusicAlbum` (music album, sleep-story playlist, DTC course/series). NOT
 * single tracks, soundscapes, clinical, mindshift, or dailies.
 */
export function shuffleCapabilityForControls(
    controls: AudioPlayerControls
): boolean {
    return (
        controls === AudioPlayerControls.Playlist ||
        controls === AudioPlayerControls.MusicAlbum
    );
}

/**
 * Whether the content type supports the playback-speed control.
 * Allowlist so a future `AudioPlayerControls` value defaults to
 * `false` (no speed control) unless explicitly added here
 */
export function supportsPlaybackSpeedForControls(
    controls: AudioPlayerControls
): boolean {
    return (
        controls === AudioPlayerControls.Playlist ||
        controls === AudioPlayerControls.SingleLoopable ||
        controls === AudioPlayerControls.Regular ||
        controls === AudioPlayerControls.Infinite
    );
}

/**
 * Whether the content type can be added to a user playlist from the player.
 * Allowlist so a future `AudioPlayerControls` value defaults to `false`.
 * Soundscapes and infinite players never show the control.
 */
export function supportsAddToPlaylistForControls(
    controls: AudioPlayerControls
): boolean {
    return (
        controls === AudioPlayerControls.Regular ||
        controls === AudioPlayerControls.Playlist ||
        controls === AudioPlayerControls.SingleLoopable ||
        controls === AudioPlayerControls.MusicAlbum ||
        controls === AudioPlayerControls.MusicSingle
    );
}
